// @ts-strict-ignore
import {immerable} from 'immer';
import mapValues from 'lodash/mapValues';

import {MinimalProjectMetadata, ProjectMetadata, ProjectMetadataMap} from 'app_api/projects/types';
import {
  FeatureGate,
  FeatureGatesValue,
  FeatureGateTier,
  INTERNAL_PROJECTS,
  Permission,
  ProjectFeatureFlag,
  ProjectType,
  RestrictedReport,
  RestrictEntitySaving,
} from 'common/report/constants';
import {extendWorkspace, toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';
import {apiClient} from 'common/util/api-client';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {getFeatureFlagUrlOverrides} from 'common/util/url';
import {BlockedTimeWindow, Timezone} from 'common/constants';
import {DEFAULT_MAX_DATA_RETENTION_DAYS} from 'common/types/app-api/billing_bump/constants';
import {DefaultShareMode} from 'common/widgets/sharing/types';
import {DEFAULT_WEEKEND_DURATION, DEFAULT_WEEKEND_START_DAY} from 'common/util/date';
import {isRetentionAwarenessEnabled} from 'common/report/util/feature-sampling';

import {appApiUrl} from '../utils';

// "minimal" project model used in collections (as opposed to richer "CurrentProject")
export class Project {
  [immerable] = true;

  public declare domain: string;
  public declare hasWorkspaces: boolean;
  public declare id: number;
  public declare isDemo: boolean;
  public declare name: string;
  public declare organizationId: number;
  public declare timePeriodSettings: {
    weekStartDay: number;
    quarterStartMonth: number;
    weekendStartDay: number;
    weekendDuration: number;
  };
  public declare experimentSettings: {
    eventName?: string;
    nameProperty?: Obj;
    variantProperty?: Obj;
    config?: {
      schema?: `separate` | `combined` | `dictionary` | `list`;
      separator?: string;
    };
  };
  public declare type: ProjectType;
  public declare workspaces: {[key: string]: WorkspaceObj};

  constructor(projectAttrs: MinimalProjectMetadata) {
    this.domain = projectAttrs.domain;
    this.hasWorkspaces = projectAttrs.has_workspaces;
    this.id = projectAttrs.id;
    this.isDemo = projectAttrs.is_demo;
    this.name = projectAttrs.name;
    this.organizationId = projectAttrs.organization_id;
    this.type = projectAttrs.type;

    this.timePeriodSettings = {
      weekStartDay: projectAttrs.time_period_settings?.week_start_day ?? 0,
      quarterStartMonth: projectAttrs.time_period_settings?.quarter_start_month ?? 0,
      weekendStartDay: projectAttrs.time_period_settings?.weekend_start_day ?? DEFAULT_WEEKEND_START_DAY,
      weekendDuration: projectAttrs.time_period_settings?.weekend_duration ?? DEFAULT_WEEKEND_DURATION,
    };
    this.experimentSettings = {
      eventName: projectAttrs.experiment_settings?.event_name,
      nameProperty: projectAttrs.experiment_settings?.name_property,
      variantProperty: projectAttrs.experiment_settings?.variant_property,
      config: {
        schema: projectAttrs.experiment_settings?.config?.schema as `separate` | `combined` | `dictionary` | `list`,
        separator: projectAttrs.experiment_settings?.config?.separator,
      },
    };
    this.workspaces = mapValues(projectAttrs.workspaces, (minimalWorkspace) => extendWorkspace({}, minimalWorkspace));
  }

  public isRollupProject(): boolean {
    return this.type === ProjectType.Rollup;
  }
}

interface ExternalHelpLink {
  linkName: string;
  linkURL: string;
}

interface Role {
  id?: number;
  name?: string;
}
export interface RetentionPolicy {
  created: number;
  retentionPeriod: number;
}

interface Blocking {
  isBlockedEvents: boolean;
  isBlockedPeople: boolean;
  blockedReplayWindows?: BlockedTimeWindow[];
  label: string;
  isBlockedFeatureFlags: boolean;
  isBlockedExperiments: boolean;
}

interface CreditInfo {
  appliesTo?: string;
  creditTypeId?: number;
  endDate?: string;
  initialValue?: string;
  name?: string;
  remainingBalance?: string;
  state?: string;
}

interface CreditBalanceInfo {
  creditUsageAmount: number;
  initialValue: number;
  remainingBalance: number;
}

enum CreditState {
  Valid = `valid`,
  Redeemed = `redeemed`,
  Expired = `expired`,
  NotEffective = `not_effective`,
}

// Note: if needing to change these values for whatever reason, make sure the enum values match with CrossProjectSetting located in project/models.py
export enum BoardMoveSetting {
  NOT_ALLOWED = `Disallowed`,
  ALLOWED_WITHIN_ORG = `Allowed within org`,
  ALLOWED_ALL_ORGS = `Allowed all orgs`,
}

export enum AccountPlanType {
  Free = `free`,
  Growth = `growth`,
  Enterprise = `enterprise`,
  Unknown = `unknown`,
}

// For reference,
// SELECT DISTINCT name, id from invoicing_billingcredittype;
const STARTUP_CREDIT_TYPE_ID = 15;

export class CurrentProject extends Project {
  [immerable] = true;

  public declare autoApprovalEnabled: boolean;
  public declare bookmarkVersions: string[];
  public declare blocking: Blocking;
  public declare creditInfos: CreditInfo[];
  public declare crossProjectBoardMovingSetting: BoardMoveSetting;
  public declare defaultShareMode: DefaultShareMode;
  public declare homepageDashboardId: number;
  public declare defaultThemeId: string | number | null;
  public declare eventsPlanID: number;
  public declare eventsPlanName: string;
  public declare externalHelpLinks: ExternalHelpLink[];
  public declare hasScdSync: boolean;
  public declare hasIntegratedArb: boolean;
  public declare hasIntegratedEngage: boolean;
  public declare hasIntegratedSessionReplay: boolean;
  public declare hasPublicDashboardsEnabled: boolean;
  public declare hasSensitiveAccess: boolean;
  public declare onboardingDashboardID: number;
  public declare ownerID: number;
  public declare peoplePlanID: number;
  public declare projectAsOfDate: null | string;
  public declare role: Role;
  public declare accountEventsRetentionDays?: number | null;
  public declare sessionReplayRetentionPeriods: RetentionPolicy[];
  public declare sparkAIEnabled: boolean;
  public declare timezone: Timezone;
  public declare userCount: number;
  public declare utcOffset: number;
  public declare featureGateTier: string;
  public declare showImplementationDeadline: boolean;
  public declare isB2b: boolean;
  public declare implementationDeadline: string;
  public declare isFree: boolean;
  public declare isSelfServe: boolean;
  public declare isEnterprise: boolean;
  public declare isStartup: boolean;

  /** Reflects the Data Governance setting */
  public declare hasEventApprovalEnabled: boolean;

  // these should be accessed through helpers hasFeatureFlag() etc
  private declare featureFlags: Set<string>;
  public declare permissions: Set<string>;
  private declare featureGates: FeatureGatesValue;
  private declare restrictedReports: Set<string>;
  private declare restrictedEntitySaving: Set<string>;

  constructor(projectAttrs: ProjectMetadata) {
    super(projectAttrs);

    this.autoApprovalEnabled = projectAttrs.auto_approval_enabled;
    this.bookmarkVersions = projectAttrs.bookmark_versions;
    this.blocking = {
      isBlockedEvents: projectAttrs.blocking.is_blocked_events,
      isBlockedPeople: projectAttrs.blocking.is_blocked_people,
      blockedReplayWindows: projectAttrs.blocking.blocked_replay_windows,
      label: projectAttrs.blocking.label,
      isBlockedFeatureFlags: projectAttrs.blocking.is_blocked_feature_flags,
      isBlockedExperiments: projectAttrs.blocking.is_blocked_experiments,
    };
    this.creditInfos =
      projectAttrs.credit_infos?.map((creditInfo) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {applies_to, credit_type_id, end_date, initial_value, name, remaining_balance, state} = creditInfo;
        return {
          appliesTo: applies_to,
          creditTypeId: credit_type_id,
          endDate: end_date,
          initialValue: initial_value,
          name,
          remainingBalance: remaining_balance,
          state,
        };
      }) || [];
    this.crossProjectBoardMovingSetting = projectAttrs.cross_project_board_move_setting as BoardMoveSetting;
    this.defaultShareMode = (projectAttrs.default_share_mode as DefaultShareMode) ?? DefaultShareMode.Default;
    this.homepageDashboardId = projectAttrs.homepage_dashboard_id;
    this.defaultThemeId = projectAttrs.default_theme_id;
    this.eventsPlanID = projectAttrs.events_plan_id;
    this.eventsPlanName = projectAttrs.events_plan_name;
    this.externalHelpLinks = projectAttrs.external_help_link.map((link) => ({
      linkName: link.link_name,
      linkURL: link.link_url,
    }));
    this.featureFlags = new Set(projectAttrs.feature_flags);
    this.featureGates = projectAttrs.feature_gates as FeatureGatesValue;
    this.featureGateTier = projectAttrs.feature_gate_tier as FeatureGateTier;
    this.hasIntegratedArb = projectAttrs.has_integrated_arb;
    this.hasIntegratedEngage = projectAttrs.has_integrated_engage;
    this.hasIntegratedSessionReplay = projectAttrs.has_integrated_session_replay;
    this.hasPublicDashboardsEnabled = projectAttrs.has_public_dashboards_enabled;
    this.hasEventApprovalEnabled = projectAttrs.has_event_approval_enabled;
    this.hasSensitiveAccess = projectAttrs.hasSensitiveAccess;
    this.hasScdSync = projectAttrs.has_scd_sync;
    this.showImplementationDeadline = projectAttrs.show_implementation_deadline;
    this.implementationDeadline = projectAttrs.implementation_deadline;
    this.onboardingDashboardID = projectAttrs.onboarding_dashboard_id;
    this.ownerID = projectAttrs.owner_id;
    this.peoplePlanID = projectAttrs.people_plan_id;
    this.permissions = new Set(projectAttrs.permissions);
    this.projectAsOfDate = projectAttrs.project_as_of_date;
    this.restrictedReports = new Set(this.featureGates?.[FeatureGate.RestrictedReports]);
    this.restrictedEntitySaving = new Set(this.featureGates?.[FeatureGate.RestrictEntitySaving]);
    this.role = projectAttrs.role || {};
    // iron/common/widgets/recording-player/query-utils.ts isRetentionExceeded expects the retention period list to be sorted by created date
    this.sessionReplayRetentionPeriods =
      projectAttrs.session_replay_retention_periods
        ?.sort((a, b) => a.created - b.created)
        .map(({created, retention_period: retentionPeriod}) => ({
          created,
          retentionPeriod,
        })) || [];
    this.accountEventsRetentionDays = projectAttrs.account_events_retention_days;
    this.sparkAIEnabled = projectAttrs.spark_ai_enabled;
    this.timezone = projectAttrs.timezone as Timezone;
    this.userCount = projectAttrs.user_count;
    this.utcOffset = projectAttrs.utc_offset;
    this.workspaces = mapValues(projectAttrs.workspaces, toWorkspaceObj);
    this.isB2b = projectAttrs.is_b2b;
    this.isFree = projectAttrs.is_free;
    this.isSelfServe = projectAttrs.is_selfserve;
    this.isEnterprise = projectAttrs.is_enterprise;
    this.isStartup = projectAttrs.is_startup ?? false;
  }

  public isInternal() {
    return INTERNAL_PROJECTS.has(this.id);
  }

  public getAccountPlanType(): AccountPlanType {
    if (this.isFree) {
      return AccountPlanType.Free;
    }
    if (this.isSelfServe) {
      return AccountPlanType.Growth;
    }
    if (this.isEnterprise) {
      return AccountPlanType.Enterprise;
    }
    return AccountPlanType.Unknown;
  }

  public hasFeatureFlag(flagName: ProjectFeatureFlag, enableInDogfoodProjects = false): boolean {
    // TODO support injecting a URLManager
    const featureFlagOverrides = getFeatureFlagUrlOverrides();
    if (Object.prototype.hasOwnProperty.call(featureFlagOverrides, flagName)) {
      return featureFlagOverrides[flagName];
    }
    return (
      this.featureFlags.has(flagName) || (enableInDogfoodProjects && this.isInternal()) || this.featureFlags.has(`dev`)
    );
  }

  public getFeatureGate<K extends keyof FeatureGatesValue>(gate: K): FeatureGatesValue[K] | null {
    return this.featureGates?.[gate];
  }

  public hasPermission(permissionName: string) {
    return this.permissions.has(permissionName);
  }

  public getStartupCreditInfos(): CreditInfo[] {
    return (
      this.creditInfos?.filter(
        ({creditTypeId, remainingBalance, state}) =>
          creditTypeId === STARTUP_CREDIT_TYPE_ID && state === CreditState.Valid && parseFloat(remainingBalance) > 0,
      ) || []
    );
  }

  public hasStartupCreditAndFreeUser(): boolean {
    return this.isFreePlanBillingAdmin() && this.getStartupCreditInfos().length > 0;
  }

  public isFreeFeatureGateTier(): boolean {
    return this.featureGateTier === `free`;
  }

  public isFreePlanBillingAdmin(): boolean {
    return this.hasPermission(Permission.UpdateOrgBilling) && this.eventsPlanName === `Free`;
  }

  public getStartupCreditBalances(): Nullable<CreditBalanceInfo> {
    const initialValue = this.getStartupCreditInfos()[0]?.initialValue;
    const remainingBalance = this.getStartupCreditInfos()[0]?.remainingBalance;
    const parsedInitialValue = parseFloat(initialValue) || 0;
    const parsedRemainingBalance = parseFloat(remainingBalance) || 0;
    return this.hasStartupCreditAndFreeUser()
      ? {
          creditUsageAmount: parsedInitialValue - parsedRemainingBalance,
          initialValue: parsedInitialValue,
          remainingBalance: parsedRemainingBalance,
        }
      : null;
  }
  public hasAccessToReport(name: RestrictedReport) {
    return !this.restrictedReports.has(name);
  }

  public canSaveEntity(name: RestrictEntitySaving) {
    return !this.restrictedEntitySaving.has(name);
  }

  public isPeopleOnlyProject() {
    return (
      (this.blocking.isBlockedEvents || !this.hasIntegratedArb) &&
      this.hasIntegratedEngage &&
      !this.blocking.isBlockedPeople
    );
  }

  public canUseCohorts(): boolean {
    return this.hasAccessToReport(RestrictedReport.Cohorts) || this.hasFeatureFlag(ProjectFeatureFlag.Cohorts);
  }

  public canUseDataGroups(): boolean {
    return this.hasFeatureFlag(ProjectFeatureFlag.B2BAnalytics) || !!this.getFeatureGate(FeatureGate.B2BAnalytics);
  }

  public canUseVerifiedContent(): boolean {
    return (
      !!this.getFeatureGate(FeatureGate.VerifiedContent) || this.hasFeatureFlag(ProjectFeatureFlag.VerifiedContent)
    );
  }

  /**
   * Returns the maximum data history days,
   * taking into account the query limit and the account's data retention limit
   * returns the more restrictive of the two limits.
   */
  public getMaxDataHistoryDays(): number {
    const queryLimit = this.getFeatureGate(FeatureGate.MaxDataHistoryDays);
    const includeDataRetention = isRetentionAwarenessEnabled(this);
    if (!includeDataRetention) {
      return queryLimit ?? DEFAULT_MAX_DATA_RETENTION_DAYS; // use default if no limit is set
    }
    // If accountEventsRetentionDays returns negative or zero, treat it as no retention limit
    const dataRetentionLimit = this.accountEventsRetentionDays > 0 ? this.accountEventsRetentionDays : null;
    if (queryLimit && dataRetentionLimit) {
      return Math.min(queryLimit, dataRetentionLimit);
    }
    return dataRetentionLimit ?? queryLimit ?? DEFAULT_MAX_DATA_RETENTION_DAYS; // use default if no limit is set
  }
}

const INDEX_PATH = appApiUrl(`projects/metadata/index`);
export const allProjectsClient: ApiResourceClient<Project> = {
  index() {
    const fetches = apiClient.hydratedGet<ProjectMetadataMap>(INDEX_PATH);
    return mapValues(fetches, (fetch) =>
      fetch.then((projects) => projects && mapValues(projects, (projectAttrs) => new Project(projectAttrs))),
    );
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: INDEX_PATH});
  },
};

function projectPath(projectId) {
  return appApiUrl(`metadata`, {projectId});
}
export const currentProjectClient: ApiResourceClient<CurrentProject> = {
  index() {
    throw new Error(`currentProjectClient does not support index queries`);
  },

  get(id) {
    if (!id) {
      // GOOT for public dashboard redirecting after //metadata
      throw new Error(`project id not found`);
    }
    const fetches = apiClient.hydratedGet<ProjectMetadata>(projectPath(id));
    return mapValues(fetches, (fetch) =>
      fetch.then((projectAttrs) => projectAttrs && new CurrentProject(projectAttrs)),
    );
  },

  purgeCache(id) {
    purgeFetchCache({everything: true, path: projectPath(id)});
  },
};
